import { ItemSendEventReason } from 'owa-addins-analytics';
import { addOrReplaceNotificationMessage, removeNotificationMessage } from 'owa-addins-apis';
import { getAdapter } from 'owa-addins-apis';
import { PerformanceDatapoint } from 'owa-analytics';
import { confirm } from 'owa-confirm-dialog';
import { isFeatureEnabled } from 'owa-feature-flags';
import ExtensionEventType from 'owa-service/lib/contract/ExtensionEventType';
import WebExtNotificationTypeType from 'owa-service/lib/contract/WebExtNotificationTypeType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { format } from 'owa-strings';
import extStrings from 'owa-strings/owa-extensibility-strings';
import mailStrings from 'owa-strings/owa-mail-strings';
import {
    createItemSendEventDatapoint,
    endItemSendEventWithReason,
    endItemSendEventDatpoint,
} from 'owa-addins-analytics';
import {
    AddinCommand,
    filterSupportsSharedFolderAddinCommands,
    setUilessAddinCommand,
    getNextControlId,
    InvokeAppAddinCommandStatusCode,
    getExtensionEventByType,
    getExtensibilityState,
    isExtensibilityContextInitialized,
} from 'owa-addins-store';

/**
 * Runs add-ins that support the ItemSend event
 * Returns true if add-ins allow sending, false if one of them prevents sending.
 * If the OnSendAddinsEnabled Mailbox Policy is enabled, sending will be blocked until add-ins are available.
 */
export default async function allowItemSendEvent(hostItemIndex: string): Promise<boolean> {
    if (!isFeatureEnabled('addin-itemSendEvent')) {
        return true;
    }

    // Currently, this code runs even in Calendar quick compose (where no add-ins are available)
    const datapoint = createItemSendEventDatapoint();
    if (tryPromptIfExtensibilityUnavailable()) {
        endItemSendEventWithReason(datapoint, ItemSendEventReason.ExtensibilityUnavailable);
        return false;
    }

    let itemSendAddins = getExtensionEventByType(ExtensionEventType.ItemSend) as AddinCommand[];

    const adapter = getAdapter(hostItemIndex);
    if (adapter && adapter.isSharedItem && adapter.isSharedItem()) {
        if (isFeatureEnabled('addin-delegateAccess')) {
            // Run only shared folder add-ins
            itemSendAddins = filterSupportsSharedFolderAddinCommands(itemSendAddins);
        } else {
            // Don't run any add-ins on the shared item
            return true;
        }
    }

    removeExistingItemSendInfobars(itemSendAddins, hostItemIndex);
    for (let i = 0; i < itemSendAddins.length; i++) {
        if (
            !(await executeItemSendEvent(
                hostItemIndex,
                itemSendAddins[i] as AddinCommand,
                datapoint
            ))
        ) {
            return false;
        }

        datapoint.addCheckmark(`ItemSend${i}`);
    }

    if (itemSendAddins.length > 0) {
        endItemSendEventWithReason(datapoint, ItemSendEventReason.AllowEventTrue);
    } else {
        // Don't log if no add-ins ran during send
        datapoint.invalidate();
    }

    return true;
}

async function executeItemSendEvent(
    hostItemIndex: string,
    addinCommand: AddinCommand,
    datapoint: PerformanceDatapoint
) {
    const controlId = getNextControlId();
    const result = await setUilessAddinCommand(controlId, addinCommand, hostItemIndex);
    if (result.status !== InvokeAppAddinCommandStatusCode.Success || result.allowEvent === false) {
        addOrReplaceNotificationMessage(
            hostItemIndex,
            addinCommand.extension.Id,
            false /* persistent */,
            `${addinCommand.extension.Id}ItemSend`,
            WebExtNotificationTypeType.InformationalMessage,
            format(extStrings.itemSendEventAddinBlockEvent, addinCommand.extension.DisplayName)
        );
        endItemSendEventDatpoint(datapoint, result, addinCommand);
        return false;
    }
    return true;
}

function tryPromptIfExtensibilityUnavailable(): boolean {
    const context = getExtensibilityState().Context;
    const addinsInitialized = isExtensibilityContextInitialized();
    if (
        getUserConfiguration().SegmentationSettings.OnSendAddinsEnabled &&
        (!context || !addinsInitialized)
    ) {
        confirm(
            mailStrings.messageSendingFailTitle,
            extStrings.itemSendEventAddinErrorExtensibilityUnavailable,
            false /* resolveImmediately */,
            {
                hideCancelButton: true,
            }
        );
        return true;
    }
    return false;
}

function removeExistingItemSendInfobars(addinCommands: AddinCommand[], hostItemIndex: string) {
    addinCommands.forEach(addinCommand =>
        removeNotificationMessage(
            hostItemIndex,
            addinCommand.extension.Id,
            `${addinCommand.extension.Id}ItemSend`
        )
    );
}
